import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

export function SmoothCursor() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  // Raw mouse coordinates
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  // Fast spring for the central precision dot
  const dotX = useSpring(mouseX, { damping: 35, stiffness: 700 });
  const dotY = useSpring(mouseY, { damping: 35, stiffness: 700 });

  // Smooth, weighted spring for the trailing circle
  const ringX = useSpring(mouseX, { damping: 22, stiffness: 200, mass: 0.5 });
  const ringY = useSpring(mouseY, { damping: 22, stiffness: 200, mass: 0.5 });

  useEffect(() => {
    // Only enable on desktop/non-touch devices with fine pointer and no reduced motion
    const hasFinePointer = window.matchMedia("(pointer: fine) and (hover: hover)").matches;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!hasFinePointer || prefersReducedMotion) {
      setIsEnabled(false);
      return;
    }

    setIsEnabled(true);

    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseDown = () => setIsClicked(true);
    const handleMouseUp = () => setIsClicked(false);

    const handleMouseLeave = () => setIsVisible(false);
    const handleMouseEnter = () => setIsVisible(true);

    // Global listener to detect hover over interactive elements
    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const isInteractive = Boolean(
        target.closest(
          "a, button, input, textarea, select, [role='button'], label, .card-interactive, [data-cursor-interactive]"
        )
      );

      setIsHovered(isInteractive);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);
    document.documentElement.addEventListener("mouseleave", handleMouseLeave);
    document.documentElement.addEventListener("mouseenter", handleMouseEnter);
    window.addEventListener("mouseover", handleMouseOver, { passive: true });

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      document.documentElement.removeEventListener("mouseleave", handleMouseLeave);
      document.documentElement.removeEventListener("mouseenter", handleMouseEnter);
      window.removeEventListener("mouseover", handleMouseOver);
    };
  }, [mouseX, mouseY, isVisible]);

  if (!isEnabled) return null;

  return (
    <div className="no-print pointer-events-none fixed inset-0 z-[9999] overflow-hidden" aria-hidden="true">
      {/* Smooth Trailing Halo Ring */}
      <motion.div
        style={{
          x: ringX,
          y: ringY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          opacity: isVisible ? 1 : 0,
          scale: isClicked ? 0.8 : isHovered ? 1.6 : 1,
          borderColor: isHovered
            ? "rgba(57, 183, 233, 0.85)"
            : "rgba(57, 183, 233, 0.45)",
          backgroundColor: isHovered
            ? "rgba(57, 183, 233, 0.12)"
            : "rgba(57, 183, 233, 0.03)",
        }}
        transition={{
          scale: { type: "spring", stiffness: 350, damping: 22 },
          opacity: { duration: 0.15 },
          borderColor: { duration: 0.15 },
          backgroundColor: { duration: 0.15 },
        }}
        className="fixed top-0 left-0 w-8 h-8 rounded-full border border-brand/50 dark:border-primary/50 shadow-[0_0_12px_rgba(57,183,233,0.15)] backdrop-blur-[0.5px]"
      />

      {/* Central Precision Target Dot */}
      <motion.div
        style={{
          x: dotX,
          y: dotY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          opacity: isVisible ? 1 : 0,
          scale: isClicked ? 1.4 : isHovered ? 0.6 : 1,
        }}
        transition={{
          scale: { type: "spring", stiffness: 500, damping: 28 },
          opacity: { duration: 0.12 },
        }}
        className="fixed top-0 left-0 w-1.5 h-1.5 rounded-full bg-brand dark:bg-primary shadow-[0_0_8px_rgba(57,183,233,0.8)]"
      />
    </div>
  );
}
